import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    HostBinding,
    ChangeDetectorRef,
    input,
} from '@angular/core';
import { MatColumnDef, MatTable, MatTableDataSource } from '@angular/material/table';

import { SelectionModel } from '@angular/cdk/collections';
import { Subject } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { interval } from 'rxjs';
import { MatSort } from '@angular/material/sort';
import { areArraysEqual } from '@fi-shared/utils/are-arrays-equal';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { isDefined, isEqual } from '@fi-shared/utils';
import { FiWrapperTableAdditionalColumns } from '@fi-shared/fi-wrapper-table/fi-wrapper-table-additional-columns';

enum NestedListItemsState {
    Expanded = 'expanded',
    Collapsed = 'collapsed',
}

@Component({
    selector: 'fi-wrapper-table',
    template: `
        <mat-table multiTemplateDataRows [dataSource]="dataSource">
          <ng-content></ng-content>
        
          <ng-container matColumnDef="expandToggle">
            <mat-header-cell *matHeaderCellDef></mat-header-cell>
            <mat-cell *matCellDef="let item">
              @if (isListItemHasChildItems(item)) {
                <button
                  mat-icon-button
                  class="toggle-extended-content-visibility"
                  (click)="toggleExpandDetailItemActive($event, item)"
                  >
                  @if (!isChildrenShown(item)) {
                    <mat-icon class="fas fa-plus"></mat-icon>
                  }
                  @if (isChildrenShown(item)) {
                    <mat-icon class="fas fa-minus"></mat-icon>
                  }
                </button>
              }
            </mat-cell>
          </ng-container>
        
          <!-- Checked Column -->
          <ng-container matColumnDef="checked">
            <mat-header-cell
              class="column-show-mobile"
              [ngClass]="{ 'rounded-checkbox': isRoundedCheckboxes, 'select-all-hidden': !isSelectAllAvailable }"
              *matHeaderCellDef
              >
              @if (isCheckAllColumnHeaderShown) {
                <mat-checkbox
                  (change)="onHeaderSelectionChanged($event)"
                  [checked]="selection.hasValue() && isAllSelected()"
                  [indeterminate]="selection.hasValue() && !isAllSelected()"
                  [aria-label]="checkboxLabel()"
                  [disabled]="isHeaderCellDisabled()"
                  >
                </mat-checkbox>
              }
            </mat-header-cell>
            <mat-cell
              class="column-show-mobile"
              [ngClass]="{ 'rounded-checkbox': isRoundedCheckboxes }"
              *matCellDef="let item"
              >
              @if (!checkboxItemUnavailableFn(item)) {
                <mat-checkbox
                  (click)="$event.stopPropagation()"
                  (change)="itemSelectionChange($event, item)"
                  [checked]="selection.isSelected(item)"
                  [indeterminate]="selection.hasValue() && isChildSelected(item)"
                  [disabled]="isCheckboxItemDisabled(item)"
                  [aria-label]="checkboxLabel(item)"
                  >
                </mat-checkbox>
              }
            </mat-cell>
          </ng-container>
        
          @if (isHeaderRowShown) {
            <mat-header-row *matHeaderRowDef="columns; sticky: true"></mat-header-row>
          }
        
          <mat-row
            *matRowDef="let row; columns: columns"
            matRipple
            [matRippleDisabled]="!hasCheckedColumn"
            [ngClass]="getRowClassFn(row)"
            (click)="onRowClick($event, row)"
            >
          </mat-row>
        
          @if (isNestedList) {
            <mat-row *matRowDef="let row; columns: ['expandedDetail']" class="expanded-detail-row"></mat-row>
          }
        
          @if (isSummaryRowIncluded) {
            <mat-row *matRowDef="let row; columns: ['summaryRow']" class="summary-row"></mat-row>
          }
        
          <div *matNoDataRow>
            @if (isEmptyList && isEmptyTableHandlerShown) {
              <fi-empty-list></fi-empty-list>
            }
          </div>
        </mat-table>
        @if (loading()) {
            <fi-loading class="small block-center" />
        }
    `,
    styleUrls: ['./fi-wrapper-table.component.scss'],
    host: { class: 'fi-wrapper-table', '[class.auto-height-cells]': 'isAutoHeightCellsEnabled', '[class.cell-border-hidden]': 'isCellBorderHidden' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class FiWrapperTableComponent<T extends { Id?: number | string }>
    implements OnInit, OnChanges, AfterContentInit, OnDestroy
{
    @Input() items: T[];
    @Input() isSelectAllAvailable: boolean = true;
    @Input() isRoundedCheckboxes: boolean;
    @Input() checkedItems: T[] = [];
    @Input() allowMultiSelect: boolean = true;
    @Input() filter: string;
    @Input() filterPredicate: (data: T, filter: string) => boolean;
    @Input() sort: MatSort;

    loading = input<boolean>(false);

    @Input() isEmptyTableHandlerShown: boolean = true;
    @Input() checkboxItemDisabledFn: (item: T) => boolean;
    @Input() checkboxItemUnavailableFn: (item: T) => boolean = () => false;
    @Input() isCheckAllColumnHeaderShown: boolean = true;
    @Input() isNestedList: boolean;
    @Input() nestedArrayPropertyName: string;
    @Input() isHeaderRowShown: boolean = true;
    @Input() isSummaryRowIncluded: boolean;
    @Input() getRowClassFn: (item: T) => string = () => undefined;
    @Input() isNewScrollbarEnabled: boolean = false;
    @Input() isAutoHeightCellsEnabled: boolean = false;
    @Input() isCellBorderHidden: boolean = false;

    @HostBinding('class.fi-table-checkbox-column') @Input() hasCheckedColumn: boolean;
    @HostBinding('class.fi-table-expand-toggle-column') @Input() hasExpandToggleColumn: boolean;

    @Output() checkedItemsChange = new EventEmitter<T[]>();
    @Output() onRowItemClick = new EventEmitter<T>();

    @HostBinding('class.fi-data-first-loading')
    get firstLoading() {
        return this.loading() && !this.items?.length;
    }

    // @Content('*') allViews: ContentViewRef[];

    //@ContentChildren(MatHeaderRowDef) headerRowDefs: QueryList<MatHeaderRowDef>;
    //@ContentChildren(MatRowDef) rowDefs: QueryList<MatRowDef<T>>;
    @ContentChildren(MatColumnDef) columnDefs: QueryList<MatColumnDef>;
    @ContentChildren(FiWrapperTableAdditionalColumns)
    additionalColumnsComponents: QueryList<FiWrapperTableAdditionalColumns>;

    @ViewChild(MatTable, { static: true }) table: MatTable<T>;

    dataSource: MatTableDataSource<T>;
    columns: string[] = [];
    selection = new SelectionModel<T>(true, []);
    isEmptyList = false;

    expandDetailItemActive: T;

    constructor(private cdr: ChangeDetectorRef) {}

    ngOnInit(): void {
        if (this.filterPredicate) {
            this.dataSource.filterPredicate = this.filterPredicate;
        }
        this.selection.changed
            .pipe(takeUntil(this.ngOnDestroy$))
            .pipe(debounce(() => interval(0)))
            .subscribe(() => {
                this.checkedItemsChange.emit(this.selection.selected);
            });
    }

    isListItemHasChildItems(item: T): boolean {
        return item.hasOwnProperty(this.nestedArrayPropertyName) && !!item[this.nestedArrayPropertyName].length;
    }

    isChildrenShown(item: T): boolean {
        return isEqual(this.expandDetailItemActive, item);
    }

    getDetailExpandItemState(item: T): NestedListItemsState {
        return this.isChildrenShown(item) ? NestedListItemsState.Expanded : NestedListItemsState.Collapsed;
    }

    toggleExpandDetailItemActive($event: MouseEvent, item: T) {
        $event.stopPropagation();
        this.expandDetailItemActive = !this.isChildrenShown(item) ? item : undefined;
    }

    isChildSelected(item: T): boolean {
        if (!this.isNestedList || !item[this.nestedArrayPropertyName]?.length) return false;

        // todo: check not the only one child but in depth
        return item[this.nestedArrayPropertyName].some((childItem: T) => this.selection.isSelected(childItem));
    }

    ngOnChanges(changes: SimpleChanges): void {
        let change = changes.allowMultiSelect;
        if (change) {
            this.selection = new SelectionModel<T>(this.allowMultiSelect, []);
        }
        change = changes.items;
        if (change) {
            this.updateDataSource();
        }
        change = changes.checkedItems;
        if (change && !areArraysEqual(this.checkedItems, this.selection.selected)) {
            this.selection.clear();
            this.selection.select(...this.checkedItems);
        }
        change = changes.filter;
        if (change) {
            this.dataSource.filter = this.filter;
        }

        change = changes.sort;
        if (change) {
            this.dataSource.sort = this.sort;
        }

        change = changes.loading;
        if (change && change.previousValue === true && change.currentValue === false) {
            this.isEmptyList = !this.items?.length;
        }

        if (
            (changes.isNestedList && !changes.isNestedList.firstChange) ||
            (changes.hasCheckedColumn && !changes.hasCheckedColumn.firstChange)
        ) {
            this.updateColumnsList();
        }
    }

    private updateDataSource() {
        if (!isDefined(this.dataSource)) {
            this.dataSource = new MatTableDataSource<T>(this.items);
        } else {
            this.dataSource.data = this.items;
        }
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    ngAfterContentInit() {
        // at this moment FiWrapperTable has content but FiWrapperTableAdditionalColumns has no ViewChildren
        this.updateColumnsList();
        setTimeout(() => {
            this.updateColumnsList();

            // does nothing, so commented out
            //this.getHeaderRowDefs().forEach(headerRowDef => this.table.addHeaderRowDef(headerRowDef));
            //this.getRowDefs().forEach(rowDef => this.table.addRowDef(rowDef));
        });
    }

    private updateColumnsList() {
        // update columns after content init or if hasCheckedColumn or isNestedList input properties were changed
        this.columns = [];
        if (this.hasCheckedColumn) {
            this.columns.splice(0, 0, 'checked');
        }
        if (this.isNestedList) {
            this.columns.splice(0, 0, 'expandToggle');
        }
        this.getColumnDefs().forEach((columnDef) => {
            this.columns.push(columnDef.name);
            this.table.addColumnDef(columnDef);
        });
        const expandedDetailsColumnIndex = this.columns.indexOf('expandedDetail');
        if (expandedDetailsColumnIndex !== -1) {
            this.columns.splice(expandedDetailsColumnIndex, 1);
        }
        this.cdr.markForCheck();
    }

    private getColumnDefs(): MatColumnDef[] {
        let columnDefs = this.columnDefs.toArray();
        if (this.additionalColumnsComponents) {
            this.additionalColumnsComponents.forEach((additionalColumnsInstance) => {
                columnDefs = additionalColumnsInstance.getMergedColumnDefs(columnDefs);
            });
        }
        return columnDefs;
    }

    /*private getHeaderRowDefs(): MatHeaderRowDef[] {
        let headerRowDefs = this.headerRowDefs.toArray();
        if (this.additionalColumnsComponents) {
            this.additionalColumnsComponents.forEach(additionalHeaderRowsInstance => {
                headerRowDefs = additionalHeaderRowsInstance.getMergedHeaderRowDefs(headerRowDefs);
            });
        }
        return headerRowDefs;
    }

    private getRowDefs(): MatRowDef<T>[] {
        let rowDefs = this.rowDefs.toArray();
        if (this.additionalColumnsComponents) {
            this.additionalColumnsComponents.forEach(additionalRowsInstance => {
                rowDefs = additionalRowsInstance.getMergedRowDefs(rowDefs);
            });
        }
        return rowDefs;
    }*/

    isCheckboxItemDisabled(item: T): boolean {
        if (!isDefined(this.checkboxItemDisabledFn)) return false;
        return this.checkboxItemDisabledFn(item);
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.filteredData.filter((row) => !this.checkboxItemUnavailableFn(row)).length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        this.isAllSelected()
            ? this.selection.clear()
            : this.dataSource.filteredData
                  .filter((row) => !this.checkboxItemUnavailableFn(row))
                  .forEach((row) => this.selection.select(row));
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: T): string {
        if (!row) {
            return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.Id}`;
    }

    triggerFiltering() {
        // https://github.com/angular/components/issues/9967
        // there is no way to filtering explicitly at this moment
        this.dataSource.filter = <any>{};
    }

    onRowClick($event: MouseEvent, item: T) {
        if (!this.hasCheckedColumn || this.isCheckboxItemDisabled(item)) {
            this.onRowItemClick.emit(item);
            return;
        }

        if (this.selection.isSelected(item)) {
            this.selection.deselect(item);
        } else {
            this.selection.select(item);
        }
    }

    onHeaderSelectionChanged($event: MatCheckboxChange) {
        if (!this.allowMultiSelect) {
            this.selection.clear();
            $event.source.toggle();
        } else {
            this.masterToggle();
        }
    }

    isHeaderCellDisabled(): boolean {
        return !this.allowMultiSelect ? !this.selection.hasValue() : false;
    }

    itemSelectionChange($event: MatCheckboxChange, item: T) {
        if (this.isCheckboxItemDisabled(item)) return;
        if ($event) {
            this.selection.toggle(item);
        }
    }
}
